import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import { ellipsedText } from '../../const/EllipsedTextHelper'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { BodyTagResult } from '../../data/BodyTagFragment'
import style from './ArticleBanner.module.sass'
import { Button } from './Button'
import { Hidden } from './Hidden'
import { TagRow } from './TagRow'

type ArticleBannerProps = {
	title: string
	lead: string | undefined
	imageUrl: string | undefined
	buttonLabel: string
	tagContent?: JSX.Element | undefined
	isEmbedded?: boolean
	href: string | { pathname: string; query: { [key: string]: string | string[] | undefined } }
	negativeTags?: BodyTagResult[]
	positiveTags?: BodyTagResult[]
	loadMoreButtonContent?: JSX.Element
}

export const ArticleBanner: FunctionComponent<ArticleBannerProps> = ({
	title,
	lead,
	imageUrl,
	buttonLabel,
	positiveTags,
	negativeTags,
	tagContent,
	isEmbedded = false,
	href,
}) => {
	const lastCharacterVisible = 240

	const translate = useTranslate()
	return (
		<div className={clsx(style.root, isEmbedded && style.rootEmbedded)}>
			<div className={style.article}>
				<div className={style.imageWrapper}>
					<Link href={href} className={style.buttonLink} title={title}>
						{imageUrl ? (
							<NextImage
								src={imageUrl}
								title={title}
								layout="fill"
								objectFit="cover"
								alt={translate('alternativeText.blogPost')}
							/>
						) : (
							<NextImage
								src={tagContent ? '/images/placeholder-article.jpg' : '/images/placeholder.webp'}
								alt={translate('alternativeText.blogPostPlaceholder')}
								title={title}
								layout="fill"
								objectFit="cover"
							/>
						)}
					</Link>
				</div>

				<div className={style.body}>
					<Link href={href} className={style.buttonLink} title={title}>
						<h3 className={style.title}>{title}</h3>
					</Link>
					<div className={style.paragraph}>
						{lead && lead.length > lastCharacterVisible ? ellipsedText(lead, lastCharacterVisible) : lead}
					</div>
				</div>
				<Hidden breakpoint="mobileUp">
					{((positiveTags && positiveTags.length > 0) || (negativeTags && negativeTags.length > 0)) && (
						<>
							{positiveTags && positiveTags.length > 0 && (
								<div className={clsx(style.article, style.articleTags)}>
									<span className={style.articleTagsTitle}>
										<Translation tr="articleBanner.positive" />
									</span>
									<TagRow tags={positiveTags} />
								</div>
							)}
							{negativeTags && negativeTags.length > 0 && (
								<div className={clsx(style.article, style.articleTags, style.articleTagsNegative)}>
									<span className={style.articleTagsTitle}>
										<Translation tr="articleBanner.negative" />
									</span>
									<TagRow tags={negativeTags} />
								</div>
							)}
						</>
					)}
					{tagContent && (
						<div className={clsx(style.article, style.articleTags)}>
							<span className={style.articleTagsTitle}>
								<Translation tr="articleBanner.topics" />
							</span>
							{tagContent}
						</div>
					)}
				</Hidden>
				<div className={style.buttonWrapper}>
					<Button label={buttonLabel} url={href} />
				</div>
			</div>
			<Hidden breakpoint="mobileDown">
				{tagContent && (
					<div className={clsx(style.article, style.articleTags)}>
						<Translation tr="articleBanner.topics" />

						{tagContent}
					</div>
				)}
				{((positiveTags && positiveTags.length > 0) || (negativeTags && negativeTags.length > 0)) && (
					<>
						{positiveTags && positiveTags.length > 0 && (
							<div className={clsx(style.article, style.articleTags)}>
								<Translation tr="articleBanner.positive" />

								<TagRow tags={positiveTags} />
							</div>
						)}
						{negativeTags && negativeTags.length > 0 && (
							<div className={clsx(style.article, style.articleTags, style.articleTagsNegative)}>
								<Translation tr="articleBanner.negative" />

								<TagRow tags={negativeTags} />
							</div>
						)}
					</>
				)}
			</Hidden>
		</div>
	)
}
